import React from "react";
import { useHistory } from "react-router-dom";
import { Modal } from "../../Components/Modal";
import ContentItemForm from "../../Components/ContentItemForm";
import TaxonomyMultiSelect from "../../Components/TaxonomyMultiSelect";
import CategoryPathSelect from "../../Components/CategoryPathSelect";
import type { ContentTypeConfig } from "../../API/Models";

/* ------------------------------------------------------------------ */
/*  Content Type Picker Modal                                         */
/* ------------------------------------------------------------------ */

export interface ContentTypePickerModalProps {
	show: boolean;
	onHide: () => void;
	createableTypes: ContentTypeConfig[];
	onSelectType: (type: ContentTypeConfig) => void;
}

export const ContentTypePickerModal: React.FC<ContentTypePickerModalProps> = ({
	show,
	onHide,
	createableTypes,
	onSelectType
}) => {
	const history = useHistory();

	return (
		<Modal show={show} onHide={onHide} size="xl" centered>
			<Modal.Header closeButton>
				<Modal.Title>Create content</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<p className="text-sm text-slate-600 mb-4">Choose a content type to start creating data.</p>
				<div className="max-h-[360px] overflow-auto pr-1">
					<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
						<button
							type="button"
							onClick={() => {
								onHide();
								history.push("/new-page");
							}}
							className="rounded-xl border border-slate-200 bg-white p-4 text-left hover:border-slate-300 hover:shadow-sm transition"
						>
							<p className="text-sm font-semibold text-slate-900">Page</p>
							<p className="text-xs text-slate-500 mt-1">Create a new site page.</p>
						</button>
						{createableTypes.map((type) => (
							<button
								key={type.id ?? type.title}
								type="button"
								onClick={() => onSelectType(type)}
								className="rounded-xl border border-slate-200 bg-white p-4 text-left hover:border-slate-300 hover:shadow-sm transition"
							>
								<p className="text-sm font-semibold text-slate-900">{type.title}</p>
								<p className="text-xs text-slate-500 mt-1">{type.description ?? "Custom content type."}</p>
							</button>
						))}
					</div>
				</div>
			</Modal.Body>
			<Modal.Footer>
				<button
					type="button"
					onClick={onHide}
					className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
				>
					Cancel
				</button>
			</Modal.Footer>
		</Modal>
	);
};

/* ------------------------------------------------------------------ */
/*  Create Content Form Modal                                         */
/* ------------------------------------------------------------------ */

export interface CreateContentFormModalProps {
	show: boolean;
	onHide: () => void;
	createType: ContentTypeConfig | null;
	createValues: Record<string, any>;
	setCreateValues: React.Dispatch<React.SetStateAction<Record<string, any>>>;
	createErrors: Record<string, string>;
	createFormError: string | null;
	creating: boolean;
	categories: any[];
	taxonomies: any[];
	onSubmit: () => void;
}

export const CreateContentFormModal: React.FC<CreateContentFormModalProps> = ({
	show,
	onHide,
	createType,
	createValues,
	setCreateValues,
	createErrors,
	createFormError,
	creating,
	categories,
	taxonomies,
	onSubmit
}) => {
	return (
		<Modal show={show} onHide={onHide} size="xl" centered>
			<Modal.Header closeButton>
				<Modal.Title>{createType?.title ? `Create ${createType.title}` : "Create content"}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{createFormError && (
					<div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700 mb-3">
						{createFormError}
					</div>
				)}
				{createType ? (
					<div className="space-y-4">
						<ContentItemForm
							fields={createType.fields ?? []}
							value={createValues}
							onChange={setCreateValues}
							errors={createErrors}
							disabled={creating}
						/>
						{createType.supportsCategory && (categories ?? []).length > 0 && (
							<div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
								<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Categories</p>
								<CategoryPathSelect
									categories={categories}
									value={(createValues?.categoryPathIds ?? []) as string[]}
									onChange={(next) => setCreateValues((prev) => ({ ...prev, categoryPathIds: next }))}
									placeholder="Assign category path"
									helper="Choose a single category path for this content."
									disabled={creating}
								/>
							</div>
						)}
						{createType.supportsTaxonomy && (
							<div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
								<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Taxonomies</p>
								<TaxonomyMultiSelect
									options={taxonomies}
									value={(createValues?.taxonomyIds ?? []).filter(Boolean)}
									onChange={(next) => setCreateValues((prev) => ({ ...prev, taxonomyIds: next }))}
									placeholder="Assign taxonomies"
									helper="Filter and select taxonomies for this content."
									disabled={creating}
								/>
							</div>
						)}
					</div>
				) : (
					<p className="text-sm text-slate-600">Select a content type to continue.</p>
				)}
			</Modal.Body>
			<Modal.Footer>
				<button
					type="button"
					onClick={onHide}
					className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
				>
					Cancel
				</button>
				<button
					type="button"
					onClick={onSubmit}
					disabled={creating || !createType}
					className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
				>
					{creating ? "Creating\u2026" : "Create"}
				</button>
			</Modal.Footer>
		</Modal>
	);
};
